/**
 * Settings → Notifications.
 *
 * Layout (top → bottom):
 *   1. Master kill-switch.
 *   2. Opt-out categories — partner responses, promotions.
 *   3. Always-on (locked) — purchases (transactional receipts) and gifts /
 *      partner deliveries. These are never gated by the user.
 *   4. Morning transit brief — its toggle, followed immediately by the delivery
 *      time + tone controls it owns (shown only while the brief is on).
 *
 * Master gates every opt-out row. Mutations are optimistic via usePatchMePrefs.
 */

import { useTranslation } from 'react-i18next';
import { MeSubpageShell } from '../../components/chrome/MeSubpageShell';
import { Toggle } from '../../components/settings/Toggle';
import { useMe, type NotificationPrefs } from '../../hooks/useMe';
import { usePatchMePrefs } from '../../hooks/usePatchMePrefs';
import { requestBotWriteAccess } from '../../lib/telegram';
import styles from './Notifications.module.css';

// User opt-out categories (master gates them). Purchases is intentionally NOT
// here — receipts/payment confirmations are transactional and always send.
type OptOutKey = 'partnerActivity' | 'promo';
const OPT_OUT_KEYS: OptOutKey[] = ['partnerActivity', 'promo'];

const TONE_OPTIONS: Array<'gentle' | 'direct' | 'poetic'> = ['gentle', 'direct', 'poetic'];

export function Notifications(): JSX.Element {
  const { t } = useTranslation(['settings', 'common']);
  const { data: me, isLoading, isError } = useMe();
  const patch = usePatchMePrefs();

  const prefs = me?.notificationPrefs;
  const masterOn = prefs?.master ?? false;

  const onToggle = (key: keyof NotificationPrefs, next: boolean | number | string): void => {
    if ((key === 'dailyAdvice' || key === 'master') && next === true) {
      void requestBotWriteAccess();
    }
    patch.mutate({ notificationPrefs: { [key]: next } as Partial<NotificationPrefs> });
  };

  const tx = (key: string): string => t(`notifications.${key}`, { ns: 'settings' });

  /** Locked, always-on row (checked + disabled) — purchases, gifts, etc. */
  const lockedRow = (key: 'purchases' | 'alwaysOn', testId: string): JSX.Element => (
    <div className={styles.row} key={key}>
      <div className={styles.rowText}>
        <p className={styles.rowLabel}>{tx(`${key}.label`)}</p>
        <p className={styles.rowDescription}>{tx(`${key}.description`)}</p>
      </div>
      <Toggle checked disabled onChange={() => {}} label={tx(`${key}.label`)} data-testid={testId} />
    </div>
  );

  const masterLabel = tx('master.label');

  return (
    <MeSubpageShell title={tx('title')} data-testid="settings-notifications-page">
      {isLoading && <p data-testid="notifications-loading">{t('states.loading', { ns: 'common' })}</p>}
      {isError && (
        <p role="alert" data-testid="notifications-error">{tx('loadError')}</p>
      )}
      {!isLoading && !isError && prefs && (
        <>
          {/* 1 ── Master kill-switch ─────────────────────────────────────── */}
          <section className={styles.section} aria-label={masterLabel}>
            <div className={styles.row}>
              <div className={styles.rowText}>
                <p className={styles.rowLabel}>{masterLabel}</p>
                <p className={styles.rowDescription}>{tx('master.description')}</p>
              </div>
              <Toggle
                checked={masterOn}
                onChange={(next) => onToggle('master', next)}
                label={masterLabel}
                data-testid="notif-toggle-master"
              />
            </div>
          </section>

          {/* 2 ── Opt-out categories ─────────────────────────────────────── */}
          <section className={styles.section} aria-label={tx('title')}>
            {OPT_OUT_KEYS.map((k) => {
              const label = tx(`${k}.label`);
              return (
                <div className={styles.row} key={k}>
                  <div className={styles.rowText}>
                    <p className={styles.rowLabel}>{label}</p>
                    <p className={styles.rowDescription}>{tx(`${k}.description`)}</p>
                  </div>
                  <Toggle
                    checked={prefs[k]}
                    onChange={(next) => onToggle(k, next)}
                    label={label}
                    disabled={!masterOn}
                    data-testid={`notif-toggle-${k}`}
                  />
                </div>
              );
            })}
          </section>

          {/* 3 ── Always-on (locked) ─────────────────────────────────────── */}
          <section className={styles.section} aria-label={tx('alwaysOn.label')}>
            {lockedRow('purchases', 'notif-toggle-purchases')}
            {lockedRow('alwaysOn', 'notif-toggle-alwaysOn')}
          </section>

          {/* 4 ── Morning brief + its own delivery controls ──────────────── */}
          <section className={styles.section} aria-label={tx('dailyAdvice.label')}>
            <div className={styles.row}>
              <div className={styles.rowText}>
                <p className={styles.rowLabel}>{tx('dailyAdvice.label')}</p>
                <p className={styles.rowDescription}>{tx('dailyAdvice.description')}</p>
              </div>
              <Toggle
                checked={prefs.dailyAdvice}
                onChange={(next) => onToggle('dailyAdvice', next)}
                label={tx('dailyAdvice.label')}
                disabled={!masterOn}
                data-testid="notif-toggle-dailyAdvice"
              />
            </div>

            {prefs.dailyAdvice && masterOn && (
              <>
                <div className={styles.row}>
                  <div className={styles.rowText}>
                    <p className={styles.rowLabel}>{tx('dailyAdvice.time')}</p>
                  </div>
                  <select
                    data-testid="daily-time"
                    value={prefs.dailyAdviceHour}
                    onChange={(e) => onToggle('dailyAdviceHour', Number(e.target.value))}
                    className={styles.select}
                  >
                    {Array.from({ length: 24 }, (_, h) => (
                      <option key={h} value={h}>
                        {String(h).padStart(2, '0')}:00
                      </option>
                    ))}
                  </select>
                </div>
                <div className={styles.row}>
                  <div className={styles.rowText}>
                    <p className={styles.rowLabel}>{tx('dailyAdvice.tone')}</p>
                  </div>
                  <select
                    data-testid="daily-tone"
                    value={prefs.dailyAdviceTone}
                    onChange={(e) => onToggle('dailyAdviceTone', e.target.value)}
                    className={styles.select}
                  >
                    {TONE_OPTIONS.map((tone) => (
                      <option key={tone} value={tone}>
                        {tx(`dailyAdvice.tone_${tone}`)}
                      </option>
                    ))}
                  </select>
                </div>
              </>
            )}
          </section>

          {patch.isError && (
            <p role="alert" data-testid="notifications-save-error">
              {tx('saveError')}
            </p>
          )}
        </>
      )}
    </MeSubpageShell>
  );
}
